import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Grid, 
  Paper, 
  Typography, 
  Button, 
  Card, 
  CardContent, 
  CardActions,
  Divider,
  Box,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Description as DocumentIcon,
  CloudUpload as UploadIcon,
  Search as SearchIcon,
  Assignment as KnowledgeIcon
} from '@mui/icons-material';
import { documentApi, knowledgeEntryApi, Document, KnowledgeEntry } from '../services/api';
import PageHeader from '../components/PageHeader';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [recentEntries, setRecentEntries] = useState<KnowledgeEntry[]>([]);
  const [stats, setStats] = useState({
    totalDocuments: 0,
    totalEntries: 0,
    unindexedDocuments: 0
  });

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        const allDocuments = await documentApi.getAllDocuments();
        const docList = Array.isArray(allDocuments) ? allDocuments : [];
        setDocuments(docList.slice(0, 5));

        const allEntries = await knowledgeEntryApi.getAllEntries();
        const entryList = Array.isArray(allEntries) ? allEntries : [];
        setRecentEntries(entryList.slice(0, 5));

        const unindexedDocs = await documentApi.getUnindexedDocuments();
        const unindexedList = Array.isArray(unindexedDocs) ? unindexedDocs : [];

        setStats({
          totalDocuments: docList.length,
          totalEntries: entryList.length,
          unindexedDocuments: unindexedList.length,
        });

        setLoading(false);
      } catch (err) {
        console.error('获取仪表盘数据失败:', err);
        setError('获取数据失败，请检查后端是否在 13085 运行，以及 Nginx 是否配置了 /knowledgeWeb/api/ 反代');
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const handleIndexUnindexed = async () => {
    try {
      const unindexedDocs = await documentApi.getUnindexedDocuments();
      if (unindexedDocs.length > 0) {
        // 索引第一个未索引的文档
        await knowledgeEntryApi.indexDocument(unindexedDocs[0].id);
        alert('索引任务已开始，请稍后查看结果');
      } else {
        alert('没有需要索引的文档');
      }
    } catch (err) {
      console.error('索引文档失败:', err);
      alert('索引文档失败，请重试');
    }
  };

  const handleGenerateEmbeddings = async () => {
    try {
      await knowledgeEntryApi.generateEmbeddings();
      alert('嵌入向量生成任务已开始，请稍后查看结果');
    } catch (err) {
      console.error('生成嵌入向量失败:', err);
      alert('生成嵌入向量失败，请重试');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box className="page-container">
      <PageHeader
        title="仪表盘"
        subtitle="知识库运行概览与常用快捷操作"
      />

      {/* 统计卡片 */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {[
          {
            label: '总文档数',
            value: stats.totalDocuments,
            icon: <DocumentIcon />,
            color: '#2563EB',
            bg: 'rgba(37, 99, 235, 0.08)',
          },
          {
            label: '知识条目数',
            value: stats.totalEntries,
            icon: <KnowledgeIcon />,
            color: '#6366F1',
            bg: 'rgba(99, 102, 241, 0.08)',
          },
          {
            label: '未索引文档',
            value: stats.unindexedDocuments,
            icon: <UploadIcon />,
            color: stats.unindexedDocuments > 0 ? '#DC2626' : '#059669',
            bg: stats.unindexedDocuments > 0 ? 'rgba(220, 38, 38, 0.08)' : 'rgba(5, 150, 105, 0.08)',
          },
        ].map((stat) => (
          <Grid item xs={12} sm={4} key={stat.label}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: stat.bg,
                  color: stat.color,
                }}
              >
                {stat.icon}
              </Box>
              <Box>
                <Typography variant="h5">{stat.value}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.label}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* 快速操作 */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          快速操作
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<UploadIcon />}
              fullWidth
              onClick={() => navigate('/upload')}
            >
              上传文档
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button 
              variant="contained" 
              color="secondary" 
              startIcon={<SearchIcon />}
              fullWidth
              onClick={() => navigate('/search')}
            >
              搜索知识
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button 
              variant="outlined" 
              color="primary" 
              fullWidth
              onClick={handleIndexUnindexed}
              disabled={stats.unindexedDocuments === 0}
            >
              索引文档
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button 
              variant="outlined" 
              color="secondary" 
              fullWidth
              onClick={handleGenerateEmbeddings}
            >
              生成嵌入向量
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* 最近文档和知识条目 */}
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              最新文档
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {documents.length > 0 ? (
              documents.map((doc) => (
                <Card key={doc.id} sx={{ mb: 2 }} className="card-hover" variant="outlined">
                  <CardContent>
                    <Typography variant="h6">{doc.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {doc.description || '无描述'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      上传于: {new Date(doc.createdAt).toLocaleString()}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button size="small" onClick={() => navigate(`/documents/${doc.id}`)}>
                      查看详情
                    </Button>
                  </CardActions>
                </Card>
              ))
            ) : (
              <Typography>暂无文档</Typography>
            )}
            {documents.length > 0 && (
              <Button 
                variant="text" 
                color="primary" 
                fullWidth
                onClick={() => navigate('/documents')}
              >
                查看全部
              </Button>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              最新知识条目
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {recentEntries.length > 0 ? (
              recentEntries.map((entry) => (
                <Card key={entry.id} sx={{ mb: 2 }} className="card-hover" variant="outlined">
                  <CardContent>
                    <Typography variant="body1">
                      {entry.content.length > 100 
                        ? `${entry.content.substring(0, 100)}...` 
                        : entry.content}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      来自文档: {entry.document.title}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button size="small" onClick={() => navigate(`/documents/${entry.document.id}`)}>
                      查看文档
                    </Button>
                  </CardActions>
                </Card>
              ))
            ) : (
              <Typography>暂无知识条目</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard; 